import { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Button, Container, Form, InputGroup } from "react-bootstrap";
import { useUser } from "../contexts/user";
import { Grid } from "@mui/material";
import ModalEditData from "../components/ModalEditData";
import ModalAddSatellite from "../components/ModalAddSatellite";
import SatelliteCard from "../components/SatelliteCard";
import SearchModal from "../components/SearchModal";
import Loading from "../components/Loading";
import { useSatellite } from "../contexts/SatelliteContext";
import { searchSats } from "../utils/Api";
import { LuInfo, LuFilter, LuList, LuX, LuArrowUpRight, LuArrowDownRight, LuPlus } from "react-icons/lu";
import { IoIosSearch } from "react-icons/io";
import "../css/Home.css";
import { useInView } from "react-intersection-observer";
import { toast } from "react-toastify";

const Home = () => {
    const BATCH = 18;
    const { isTokenResolved, isLoggedIn } = useUser();
    const { satNos, fetchSatData, isLoading } = useSatellite();
    const [filters, setFilters] = useState({});
    const [showSatModal, setShowSatModal] = useState(false);
    const [hrrChecked, setHrrChecked] = useState(false);
    const [selectedSat, setSelectedSat] = useState(null);
    const [showAddSatModal, setShowAddSatModal] = useState(false);
    const [showQueryModal, setShowQueryModal] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [visibleSats, setVisibleSats] = useState({});
    const [isDescending, setIsDescending] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [endIndex, setEndIndex] = useState(BATCH);
    const [hasSearched, setHasSearched] = useState(false);

    const [fetchDataCount, setFetchDataCount] = useState(0);
    const [loadMoreDataCount, setLoadMoreDataCount] = useState(0);

    const { ref, inView } = useInView({
        threshold: 1,
        rootMargin: "100px"
    });

    const Sats = useMemo(() => {
        const sats = searchResults.length > 0 ? searchResults : satNos;
        const revSats = isDescending ? [...sats].reverse() : sats;
        return revSats.slice(0, endIndex);
    }, [endIndex, searchResults, satNos, isDescending]);

    useEffect(() => {
        const fetchData = async () => {
            if (Sats.length > 0 && isTokenResolved) {
                try {
                    const data = await fetchSatData(Sats);
                    setVisibleSats(data);
                    setFetchDataCount((prevCount) => prevCount + 1);
                } catch (error) {
                    toast.error(`Error fetching satellite data: ${error.message}`);
                }
            }
        };

        fetchData();
    }, [fetchSatData, isTokenResolved, Sats, isDescending]);

    const loadMoreData = useCallback(async () => {
        if (!isLoading && endIndex < (searchResults.length > 0 ? searchResults.length : satNos.length)
            && loadMoreDataCount < fetchDataCount) {
            try {
                setLoadMoreDataCount((prevCount) => prevCount + 1);
                setEndIndex((prevEnd) => Math.min(prevEnd + BATCH,
                    (searchResults.length > 0 ? searchResults.length : satNos.length)));
            } catch (error) {
                toast.error(`Error loading more satellite data: ${error.message}`);
            }
        }
    }, [endIndex, isLoading, satNos.length, searchResults.length, fetchDataCount, loadMoreDataCount]);

    useEffect(() => {
        if (inView && !isLoading) {
            loadMoreData(); // eslint-disable-line react-hooks/set-state-in-effect -- intersection observer trigger
        }
    }, [inView, isLoading, loadMoreData]);

    const executeSearch = useCallback(async (currentFilters, currentSearchValue) => {
        try {
            const results = await searchSats(currentFilters, currentSearchValue.split(",").map((str) => str.trim()));
            setSearchResults(results);
            setEndIndex(BATCH);
            setHasSearched(true);
        } catch (error) {
            toast.error(`Error searching satellites: ${error.message}`);
        }
    }, [BATCH]);

    const removeFilter = useCallback((key) => {
        if (key === "Rank" && filters[key]?.operator === ">" && filters[key]?.start === "0" && !filters[key]?.end) {
            setHrrChecked(false);
        }
        const updatedFilters = Object.keys(filters)
            .filter((filterKey) => filterKey !== key)
            .reduce((obj, k) => {
                obj[k] = filters[k];
                return obj;
            }, {});
        setFilters(updatedFilters);
        executeSearch(updatedFilters, searchValue);
    }, [filters, searchValue, executeSearch]);

    const handleSearchChange = (event) => {
        setSearchValue(event.target.value);
    };

    const handleSearch = () => {
        executeSearch(filters, searchValue);
    };

    const handleHrrToggle = () => {
        const newHrrChecked = !hrrChecked;
        setHrrChecked(newHrrChecked);

        if (newHrrChecked) {
            const newFilter = {
                "Rank": {
                    param: "Rank",
                    operator: ">",
                    start: "0",
                    end: null
                }
            };
            const updatedFilters = {...filters, ...newFilter};
            setFilters(updatedFilters);
            executeSearch(updatedFilters, searchValue);
        } else {
            const updatedFilters = {...filters};
            delete updatedFilters.Rank;
            setFilters(updatedFilters);
            executeSearch(updatedFilters, searchValue);
        }
    };

    const onSatUpdate = useCallback(async (satNo) => {
        try {
            const response = await fetchSatData([satNo], true);
            setVisibleSats((prevVisibleSats) => {
                const newSats = {...prevVisibleSats};
                newSats[satNo] = response[satNo];
                return newSats;
            });
            if (selectedSat?.SatNo === satNo) {
                setSelectedSat(response[satNo]);
            }
        } catch (error) {
            toast.error(`Error updating satellite data: ${error.message}`);
        }
    }, [fetchSatData, selectedSat]);

    const addFilter = useCallback((newFilter) => {
        const updatedFilters = { ...filters, ...newFilter };
        setFilters(updatedFilters);
        executeSearch(updatedFilters, searchValue);
    }, [filters, searchValue, executeSearch]);

    const satCount = searchResults.length > 0 ? searchResults.length : satNos.length;
    const hasFilters = Object.entries(filters).length > 0;

    return (
        <>
            <ModalEditData
                show={showSatModal}
                setShow={setShowSatModal}
                selectedSat={selectedSat}
                onSatUpdate={onSatUpdate}
            />
            <Container className="home-container">

                {/* Title row */}
                <Row className="mb-3">
                    <Col>
                        <h4 className="home-title">Satellites <span className="sat-count">({satCount})</span></h4>
                    </Col>
                </Row>

                {/* Search bar */}
                <Row className="mb-2">
                    <Col>
                        <InputGroup className="search-input-group">
                            <Form.Control
                                type="text"
                                name="search"
                                value={searchValue}
                                placeholder="Search by NORAD ID or name..."
                                onChange={handleSearchChange}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        handleSearch();
                                    }
                                }}
                            />
                            <Button
                                variant="outline-secondary"
                                title="Advanced Filters"
                                onClick={() => setShowQueryModal(true)}
                            >
                                <LuFilter /><LuList className="ms-1" />
                            </Button>
                            <Button
                                variant="warning"
                                onClick={handleSearch}
                                title="Search"
                                className="search-button"
                            >
                                <IoIosSearch style={{ fontSize: "1.1rem", color: "black" }} />
                            </Button>
                        </InputGroup>

                        {/* Applied filters */}
                        {hasFilters && (
                            <div className="applied-filters">
                                <span className="applied-filters-label">Filters:</span>
                                <div className="filter-tags">
                                    {Object.entries(filters).map(([key, { operator, start, end }]) => (
                                        <span key={key} className="filter-tag">
                                            {key} {operator} {operator === "range" ? `${start}–${end}` : start}
                                            <button
                                                className="filter-tag-remove"
                                                onClick={() => removeFilter(key)}
                                                aria-label={`Remove ${key} filter`}
                                            >
                                                <LuX />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {hasSearched && searchResults.length === 0
                            && (searchValue || Object.keys(filters).length > 0) && (
                            <p className="no-results-text">No matching satellites found</p>
                        )}
                    </Col>
                </Row>

                {/* Info notice */}
                <Row className="mb-3">
                    <Col>
                        <p className="info-notice">
                            <LuInfo className="me-1" />
                            This is publicly available information and was not provided through Intel channels.
                        </p>
                    </Col>
                </Row>

                {/* Toolbar row */}
                <Row className="mb-4">
                    <Col className="toolbar-row">
                        <div className="toolbar-left">
                            <Button
                                variant="outline-light"
                                size="sm"
                                onClick={() => { setIsDescending(!isDescending); setEndIndex(BATCH); }}
                            >
                                {isDescending
                                    ? <><LuArrowDownRight className="me-1" />Descending</>
                                    : <><LuArrowUpRight className="me-1" />Ascending</>
                                }
                            </Button>

                            {isLoggedIn && (
                                <Button
                                    size="sm"
                                    variant={hrrChecked ? "success" : "outline-light"}
                                    onClick={handleHrrToggle}
                                >
                                    HRR Only
                                </Button>
                            )}
                        </div>

                        {isLoggedIn && (
                            <Button
                                size="sm"
                                variant="warning"
                                onClick={() => setShowAddSatModal(true)}
                            >
                                <LuPlus className="me-1" />Add Satellite
                            </Button>
                        )}
                    </Col>
                </Row>

                <SearchModal show={showQueryModal} setClose={() => setShowQueryModal(false)} addFilter={addFilter} />

                {Object.keys(satNos).length === 0 && <Loading />}

                {visibleSats && (
                    <Grid container spacing={3} sx={{ justifyContent: "center" }}>
                        {(isDescending ? Object.values(visibleSats).reverse() : Object.values(visibleSats)).map(
                            (sat, index) => (
                                <Grid item key={sat?.SatNo ?? `sat-${index}`} xs={12} sm={12} md={6} lg={4}>
                                    <SatelliteCard
                                        setShowSatModal={setShowSatModal}
                                        sat={sat}
                                        loggedIn={isLoggedIn}
                                        setSelectedSat={setSelectedSat}
                                    />
                                </Grid>
                            )
                        )}
                    </Grid>
                )}

                {isTokenResolved && !isLoading && <div ref={ref} style={{ height: "1px" }} />}
            </Container>

            <ModalAddSatellite show={showAddSatModal} setShow={setShowAddSatModal} />
        </>
    );
};

export default Home;
